import { create as createNiceModal, useModal } from '@ebay/nice-modal-react'
import { Error as ErrorIcon } from '@mui/icons-material'
import { I18n } from 'common'
import { observer } from 'mobx-react-lite'
import { ReactElement } from 'react'
import { useUiStateStore } from 'src/store/uiState/UiStateStore'
import { defaultTheme } from 'src/theme'
import { Button, SimpleModal } from 'src/ui-components'
import styled from 'styled-components'

interface OrderTimeoutErrorProps {
  onTryAgain: () => void
  onSaveAsQuote: () => void
}

export const OrderTimeoutError = createNiceModal<OrderTimeoutErrorProps>(
  observer(({ onTryAgain, onSaveAsQuote }): ReactElement => {
    const modal = useModal()
    const { setShowContactDrawer } = useUiStateStore()

    const handleTryAgain = () => {
      modal.remove()
      onTryAgain()
    }

    const handleSaveAsQuote = () => {
      modal.remove()
      onSaveAsQuote()
    }

    const handleContactUs = () => {
      modal.remove()
      setShowContactDrawer(true)
    }

    return (
      <SimpleModal
        show={modal.visible}
        title="oopsSomethingWentWrong"
        size="lg"
        onCancel={() => modal.remove()}
        borderBottomHeader
        icon={<StyledErrorIcon />}
      >
        <Content>
          <BoldText>
            <I18n name="itsTakingLongerThanUsualToProcessYourOrder" />
          </BoldText>
          <DescriptionText>
            <I18n name="pleaseTryAgainAndContactYourPartsSupplier" />
          </DescriptionText>
          <InfoBanner>
            <InfoIcon />
            <I18n name="youCanAlsoCloseThisAndReturnLater" />
          </InfoBanner>
          <Divider />
          <ButtonsContainer>
            <Button
              text="contactUs"
              onClick={handleContactUs}
              variant="secondary"
            />
            <Button
              text="saveAsQuote"
              onClick={handleSaveAsQuote}
              variant="secondary"
            />
            <Button
              text="tryAgain"
              onClick={handleTryAgain}
              variant="primary"
            />
          </ButtonsContainer>
        </Content>
      </SimpleModal>
    )
  })
)

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 40px 20px 0;
`

const BoldText = styled.span`
  font-family: Roboto, serif;
  font-weight: bold;
  font-size: 18px;
  line-height: 24px;
  color: ${defaultTheme.colors.black};
  padding-bottom: 10px;
`

const DescriptionText = styled.span`
  font-family: Roboto, serif;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
  color: ${defaultTheme.colors.almostBlack};
  padding-bottom: 20px;
`

const Divider = styled.hr`
  width: 100%;
  border: none;
  border-top: 1px solid ${defaultTheme.colors.greyLight};
  margin-top: 20px;
  margin-bottom: 0;
`

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  align-self: stretch;
  flex-direction: row;
  gap: 10px;
  padding-top: 16px;
`

const StyledErrorIcon = styled(ErrorIcon)`
  color: ${defaultTheme.colors.red};
  margin-left: 10px;
  margin-right: 10px;
`

const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  background-color: ${defaultTheme.colors.greyVeryLight};
  padding: 10px;
  border-radius: 4px;
  width: 100%;
  font-size: 14px;
`

const InfoIcon = styled(ErrorIcon)`
  color: ${defaultTheme.colors.greyDark};
  margin-right: 8px;
`
